import { Component, OnInit, signal, computed } from '@angular/core';
import { LocationProxyService } from '../../proxies/inventory/location.proxy.service';
import { LocationNode, LocationType } from '../../models/location';
import { LocalHelperService } from '../../services/local-helper.service';
import { ModalService } from '@card-software/library';
import { LocationModalComponent } from '../common/modals/location-modal/location-modal.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../models/translation';

interface TreeNode {
  data: LocationNode;
  children?: TreeNode[];
  expanded?: boolean;
}

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
})
export class LocationsComponent implements OnInit {
  isLoading = signal(false);
  locations = signal<LocationNode[]>([]);
  treeNodes = computed(() => this.buildTreeStructure(this.locations()));

  LocationType = LocationType;

  translations: Record<string, Translation> = {
    Delete_Confirmation_Title: {
      en: 'Confirm Deletion',
      es: 'Confirmar Eliminación',
    },
    Delete_Confirmation_Message: {
      en: 'Are you sure you want to delete this location?',
      es: '¿Está seguro de que desea eliminar esta ubicación?',
    },
    Cancel: {
      en: 'Cancel',
      es: 'Cancelar',
    },
    Delete: {
      en: 'Delete',
      es: 'Eliminar',
    },
    Success_Delete_Message: {
      en: 'Location {{name}} deleted successfully',
      es: 'Ubicación {{name}} eliminada exitosamente',
    },
  };

  constructor(
    private locationProxyService: LocationProxyService,
    public localHelperTranslate: LocalHelperService,
    private modalService: ModalService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  loadLocations(): void {
    this.isLoading.set(true);
    this.locationProxyService.getAll().subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.success && response.data) {
          this.locations.set(response.data);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error loading locations:', error);
      },
    });
  }

  private buildTreeStructure(locations: LocationNode[]): TreeNode[] {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Create a map for quick lookup
    const locationMap = new Map<string, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // First pass: create all tree nodes
    locations.forEach((location) => {
      locationMap.set(location.locationCode, {
        data: location,
        children: [],
        expanded: false,
      });
    });

    // Second pass: build the hierarchy
    locations.forEach((location) => {
      const treeNode = locationMap.get(location.locationCode);
      if (!treeNode) return;

      if (location.parentCode) {
        // Has a parent, add to parent's children
        const parentNode = locationMap.get(location.parentCode);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(treeNode);
        } else {
          // Parent not found, treat as root
          rootNodes.push(treeNode);
        }
      } else {
        // No parent, this is a root node
        rootNodes.push(treeNode);
      }
    });

    return rootNodes;
  }

  getLocationTypeName(type: LocationType): string {
    return LocationType[type];
  }

  getStorableText(isStorable: boolean): string {
    return isStorable ? 'Yes' : 'No';
  }

  openLocationModal(parentLocation?: LocationNode, locationToEdit?: LocationNode): void {
    this.modalService
      .open(
        LocationModalComponent,
        { modalClass: 'modal_small' },
        {
          parentLocation: structuredClone(parentLocation),
          locationToEdit: structuredClone(locationToEdit),
        },
      )
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          // Refresh locations list
          this.loadLocations();
        }
      });
  }

  onAddChildLocation(event: Event, location: LocationNode): void {
    event.stopPropagation();
    this.openLocationModal(location);
  }

  onEditLocation(event: Event, location: LocationNode): void {
    event.stopPropagation();
    const parent = location.parentCode ? this.locations().find((l) => l.locationCode === location.parentCode) : undefined;
    this.openLocationModal(parent, location);
  }

  deletePopup(event: Event, location: LocationNode): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'locationsConfirm',
      target: event.target as EventTarget,
      message: this.translations['Delete_Confirmation_Message'][this.translateService.currentLang as keyof Translation],
      header: this.translations['Delete_Confirmation_Title'][this.translateService.currentLang as keyof Translation],
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: this.translations['Cancel'][this.translateService.currentLang as keyof Translation],
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translations['Delete'][this.translateService.currentLang as keyof Translation],
        severity: 'danger',
      },
      accept: () => {
        this.deleteLocation(location);
      },
    });
  }

  deleteLocation(location: LocationNode): void {
    this.locationProxyService.deleteLocation(location.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translations['Success_Delete_Message'][this.translateService.currentLang as keyof Translation].replace(
              '{{name}}',
              this.localHelperTranslate.getLocalizedValue(location.name),
            ),
            key: 'br',
            life: 2500,
          });
          this.loadLocations();
        }
      },
      error: (error) => {
        console.error('Error deleting location:', error);
      },
    });
  }
}
